import React, { useMemo, useState, useRef, useEffect } from "react";
import {
  Paper,
  Box,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  AlertTitle,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import config from "../../../../config/licencePriceCalculator.json";
import {
  calculateTotals,
  calculateInstallationTotals,
  calculateGrandTotals,
  getPartnerIdFromLocalStorage,
  getLicenseOptions,
  ensureValidLicenseSelection,
} from "../helpers";
import { ORIGIN_INSTALLATION_ADDRESS } from "../constants";
import { loadGoogleMapsPlaces } from "../../../utils/loadGoogleMaps";
import { MAPS_API_KEY } from "../../../services/config";
import {
  IconButton,
  TextField,
  Card,
  CardContent,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

// Helper function to clamp quantity between 0 and 10
const clampQty = (qty) => {
  const num = parseInt(qty, 10);
  if (isNaN(num)) return 0;
  if (num < 0) return 0;
  if (num > 10) return 10;
  return num;
};

// Helper function to get logged-in partner ID from localStorage
const getLoggedPartnerId = () => {
  try {
    const userData = localStorage.getItem("user");
    if (!userData) return null;

    const user = JSON.parse(userData);
    const customerId = user?.customerId;
    if (!customerId) return null;

    // Normalize to number if possible
    return isNaN(Number(customerId)) ? customerId : Number(customerId);
  } catch (e) {
    return null;
  }
};

// Helper function to get MOC price for a product and market
const getProductMocPrice = (product, market = "sk") => {
  if (!product?.price) return 0;

  // If price is a number (backward compatibility), return it
  if (typeof product.price === "number") {
    return product.price;
  }

  // If price is an object, return market-specific price
  if (typeof product.price === "object" && product.price[market]) {
    return product.price[market];
  }

  // Fallback to 'sk' if market not found
  return product.price.sk || 0;
};

// Helper function to get VOC price for a product, partner, and market
const getProductVocPrice = (product, partnerId, market = "sk") => {
  if (!partnerId || !product?.partners) return null;

  // Find partner entry by matching ID (loose numeric comparison)
  for (const [key, partnerData] of Object.entries(product.partners)) {
    if (Number(partnerData.id) === Number(partnerId)) {
      // If partner price is a number (backward compatibility), return it
      if (typeof partnerData.price === "number") {
        return partnerData.price;
      }

      // If partner price is an object, return market-specific price
      if (typeof partnerData.price === "object" && partnerData.price[market]) {
        return partnerData.price[market];
      }

      // Fallback to 'sk' if market not found
      return partnerData.price.sk || null;
    }
  }

  return null;
};

/**
 * Convert EUR to CZK using rate. Rounds to 0 decimals.
 * Returns null if rate invalid (not a number or <= 0).
 */
function toCzk(eur, rate) {
  if (eur === null || eur === undefined || Number.isNaN(Number(eur)))
    return null;
  const r = Number(rate);
  if (Number.isNaN(r) || r <= 0) return null;
  return Math.round(Number(eur) * r);
}

// Product tile component
const ProductTile = ({
  product,
  quantity,
  onQuantityChange,
  t,
  loggedPartnerId,
  market,
  pagerHolderSelected,
  formatPrice,
}) => {
  const fmt = formatPrice || ((v) => (v != null && !Number.isNaN(Number(v)) ? `${Number(v).toFixed(2)} €` : "—"));
  const handleQuantityChange = (newQty) => {
    let clampedQty = clampQty(newQty);

    // Special constraint for PAGER-SYSTEM when PAGER-HOLDER-SYSTEM is selected
    if (
      product.code === "PAGER-SYSTEM" &&
      pagerHolderSelected &&
      clampedQty < 1
    ) {
      clampedQty = 1;
    }

    onQuantityChange(product.code, clampedQty);
  };

  const handleIncrement = () => {
    handleQuantityChange(quantity + 1);
  };

  const handleDecrement = () => {
    handleQuantityChange(quantity - 1);
  };

  const handleInputChange = (e) => {
    handleQuantityChange(e.target.value);
  };

  return (
    <Card sx={{ width: "100%" }}>
      <CardContent sx={{ p: 2 }}>
        <Stack spacing={1} alignItems="center">
          <Box
            sx={{
              position: "relative",
              width: 120,
              height: 120,
            }}
          >
            <Box
              component="img"
              src={product.image}
              alt={product.name}
              sx={{
                width: 120,
                height: 120,
                objectFit: "contain",
                borderRadius: 1,
              }}
            />
            {(product.url || product.pdf) && (
              <Stack
                direction="column"
                spacing={0.75}
                sx={{
                  position: "absolute",
                  bottom: 4,
                  right: 4,
                  alignItems: "center",
                  "& .MuiIconButton-root": {
                    width: 28,
                    height: 28,
                    padding: 0,
                    backgroundColor: "primary.main",
                    color: "primary.contrastText",
                    borderRadius: "50%",
                    transition: "transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease",
                    "&:hover": {
                      backgroundColor: "primary.dark",
                      transform: "scale(1.2)",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                    },
                    "& .MuiSvgIcon-root": {
                      fontSize: 16,
                    },
                  },
                }}
              >
                {product.url && (
                  <IconButton
                    size="small"
                    onClick={() =>
                      window.open(product.url, "_blank", "noopener,noreferrer")
                    }
                    title={t("pages.priceCalculator.productDetail", {
                      defaultValue: "Detail",
                    })}
                    aria-label={t("pages.priceCalculator.productDetail", {
                      defaultValue: "Detail",
                    })}
                  >
                    <OpenInNewIcon />
                  </IconButton>
                )}
                {product.pdf && (
                  <IconButton
                    size="small"
                    component="a"
                    href={product.pdf}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={t("pages.priceCalculator.productPdf", {
                      defaultValue: "PDF",
                    })}
                    aria-label={t("pages.priceCalculator.productPdf", {
                      defaultValue: "PDF",
                    })}
                  >
                    <PictureAsPdfIcon />
                  </IconButton>
                )}
              </Stack>
            )}
          </Box>
          <Typography
            variant="body2"
            sx={{
              textAlign: "center",
              minHeight: 40,
              display: "flex",
              alignItems: "center",
            }}
          >
            {product.name}
          </Typography>

          {/* Price display */}
          <Stack spacing={0.5} alignItems="center">
            {(() => {
              const mocPrice = getProductMocPrice(product, market);
              const vocPrice = loggedPartnerId
                ? getProductVocPrice(product, loggedPartnerId, market)
                : null;
              return loggedPartnerId && vocPrice !== null ? (
                <>
                  <Typography variant="caption" color="text.secondary">
                    {t("pages.priceCalculator.moc", { defaultValue: "MOC" })}:{" "}
                    {fmt(mocPrice)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t("pages.priceCalculator.voc", { defaultValue: "VOC" })}:{" "}
                    {fmt(vocPrice)}
                  </Typography>
                </>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  {t("pages.priceCalculator.price", { defaultValue: "Price" })}:{" "}
                  {fmt(mocPrice)}
                </Typography>
              );
            })()}
          </Stack>

          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton
              size="small"
              onClick={handleDecrement}
              disabled={
                quantity <= 0 ||
                (product.code === "PAGER-SYSTEM" &&
                  pagerHolderSelected &&
                  quantity <= 1)
              }
            >
              <RemoveIcon />
            </IconButton>
            <TextField
              size="small"
              type="number"
              value={quantity}
              onChange={handleInputChange}
              inputProps={{
                min:
                  product.code === "PAGER-SYSTEM" && pagerHolderSelected
                    ? 1
                    : 0,
                max: 10,
                style: { textAlign: "center", width: 60 },
              }}
              sx={{ width: 80 }}
            />
            <IconButton
              size="small"
              onClick={handleIncrement}
              disabled={quantity >= 10}
            >
              <AddIcon />
            </IconButton>
          </Stack>

          {/* Product note alert (popis) */}
          {product.note && product.note.trim() && (
            <Alert
              severity="info"
              sx={{
                fontSize: "0.75rem",
                mt: 1,
                mb: 0.5,
                py: 0.5,
                px: 1,
                width: "100%",
                "& .MuiAlert-message": {
                  width: "100%",
                },
              }}
            >
              {product.note}
            </Alert>
          )}

        </Stack>
      </CardContent>
    </Card>
  );
};

// Products summary table component
const ProductsSummaryTable = ({
  products,
  selectedProducts,
  total,
  vocTotal,
  hasAnyVoc,
  loggedPartnerId,
  market,
  t,
  formatPrice,
}) => {
  const fmt = formatPrice || ((v) => (v != null ? `${Number(v).toFixed(2)} €` : "—"));
  if (!selectedProducts || selectedProducts.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mt: 3 }}>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                {t("pages.priceCalculator.device", { defaultValue: "Device" })}
              </TableCell>
              {hasAnyVoc && (
                <>
                  <TableCell align="right">
                    {t("pages.priceCalculator.moc", { defaultValue: "MOC" })} /{" "}
                    {
                      t("pages.priceCalculator.pricePerPiece", {
                        defaultValue: "pc",
                      }).split(" / ")[1]
                    }
                  </TableCell>
                  <TableCell align="right">
                    {t("pages.priceCalculator.voc", { defaultValue: "VOC" })} /{" "}
                    {
                      t("pages.priceCalculator.pricePerPiece", {
                        defaultValue: "pc",
                      }).split(" / ")[1]
                    }
                  </TableCell>
                </>
              )}
              {!hasAnyVoc && (
                <TableCell align="right">
                  {t("pages.priceCalculator.pricePerPiece", {
                    defaultValue: "Price / pc",
                  })}
                </TableCell>
              )}
              <TableCell align="right">
                {t("pages.priceCalculator.quantityPieces", {
                  defaultValue: "Qty pc",
                })}
              </TableCell>
              {hasAnyVoc && (
                <>
                  <TableCell align="right">
                    {t("pages.priceCalculator.moc", { defaultValue: "MOC" })}{" "}
                    {t("pages.priceCalculator.total", {
                      defaultValue: "Total",
                    })}
                  </TableCell>
                  <TableCell align="right">
                    {t("pages.priceCalculator.voc", { defaultValue: "VOC" })}{" "}
                    {t("pages.priceCalculator.total", {
                      defaultValue: "Total",
                    })}
                  </TableCell>
                </>
              )}
              {!hasAnyVoc && (
                <TableCell align="right">
                  {t("pages.priceCalculator.total", { defaultValue: "Total" })}
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {selectedProducts.map((product) => {
              const quantity = products[product.code] || 0;
              const mocPrice = getProductMocPrice(product, market);
              const mocItemTotal = mocPrice * quantity;

              const vocPrice = loggedPartnerId
                ? getProductVocPrice(product, loggedPartnerId, market)
                : null;
              const vocItemTotal =
                vocPrice !== null ? vocPrice * quantity : null;

              return (
                <TableRow key={product.code}>
                  <TableCell>{product.name}</TableCell>
                  {hasAnyVoc && (
                    <>
                      <TableCell align="right">{fmt(mocPrice)}</TableCell>
                      <TableCell align="right">
                        {vocPrice !== null ? fmt(vocPrice) : "—"}
                      </TableCell>
                    </>
                  )}
                  {!hasAnyVoc && (
                    <TableCell align="right">{fmt(mocPrice)}</TableCell>
                  )}
                  <TableCell align="right">{quantity}</TableCell>
                  {hasAnyVoc && (
                    <>
                      <TableCell align="right">{fmt(mocItemTotal)}</TableCell>
                      <TableCell align="right">
                        {vocItemTotal !== null ? fmt(vocItemTotal) : "—"}
                      </TableCell>
                    </>
                  )}
                  {!hasAnyVoc && (
                    <TableCell align="right">{fmt(mocItemTotal)}</TableCell>
                  )}
                </TableRow>
              );
            })}
            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>
                {t("pages.priceCalculator.total", {
                  defaultValue: "Total",
                })}
              </TableCell>
              {hasAnyVoc && (
                <>
                  <TableCell align="right" sx={{ fontWeight: "bold" }}>
                    —
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: "bold" }}>
                    —
                  </TableCell>
                </>
              )}
              {!hasAnyVoc && (
                <TableCell align="right" sx={{ fontWeight: "bold" }}>
                  —
                </TableCell>
              )}
              <TableCell align="right" sx={{ fontWeight: "bold" }}>
                —
              </TableCell>
              {hasAnyVoc && (
                <>
                  <TableCell align="right" sx={{ fontWeight: "bold" }}>
                    {fmt(total)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: "bold" }}>
                    {fmt(vocTotal)}
                  </TableCell>
                </>
              )}
              {!hasAnyVoc && (
                <TableCell align="right" sx={{ fontWeight: "bold" }}>
                  {fmt(total)}
                </TableCell>
              )}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

const PriceCalculatorForm = () => {
  const { t } = useTranslation();
  const partnerId = getPartnerIdFromLocalStorage();
  const loggedPartnerId = getLoggedPartnerId(); // For product pricing
  const { licenses, products: configProducts } = config;
  const activeProducts = useMemo(
    () =>
      configProducts?.filter(
        (product) => product.isVisibleInPricelist === true,
      ) || [],
    [configProducts],
  );
  const licenseOptions = useMemo(
    () => getLicenseOptions(config, partnerId),
    [partnerId],
  );

  const [form, setForm] = useState({
    licenseType: "basic",
    kioskCount: 1,
    commitmentOptionKey: "monthly",
    products: {},
    productsMarket: "sk",
    czExchangeRate: 25,
    displayCurrency: "EUR",
    installation: {
      km: 0,
      travelKm: 0,
      addressText: "",
      placeId: "",
      kmManualOverride: false,
      installHours: 2,
    },
  });

  const [validationErrors, setValidationErrors] = useState({
    licenseType: false,
    kioskCount: false,
    address: false,
  });

  // Compute selected products and totals (with MOC/VOC support)
  const { selectedProducts, productsTotal, vocProductsTotal, hasAnyVoc } =
    useMemo(() => {
      const selected =
        configProducts?.filter(
          (product) => (form.products[product.code] || 0) > 0,
        ) || [];

      const mocTotal = selected.reduce((sum, product) => {
        const price = getProductMocPrice(product, form.productsMarket);
        const quantity = form.products[product.code] || 0;
        const itemTotal = price * quantity;
        return sum + (isNaN(itemTotal) ? 0 : itemTotal);
      }, 0);

      // Calculate VOC totals only if partner is logged in
      let vocTotal = 0;
      let hasVoc = false;

      if (loggedPartnerId) {
        vocTotal = selected.reduce((sum, product) => {
          const vocPrice = getProductVocPrice(
            product,
            loggedPartnerId,
            form.productsMarket,
          );
          if (vocPrice !== null) {
            hasVoc = true;
            const quantity = form.products[product.code] || 0;
            const itemTotal = vocPrice * quantity;
            return sum + (isNaN(itemTotal) ? 0 : itemTotal);
          }
          return sum;
        }, 0);
      }

      return {
        selectedProducts: selected,
        productsTotal: mocTotal,
        vocProductsTotal: vocTotal,
        hasAnyVoc: hasVoc,
      };
    }, [configProducts, form.products, form.productsMarket, loggedPartnerId]);

  // If default license isn't present, fallback to the cheapest (first option)
  React.useEffect(() => {
    if (!licenseOptions.length) return;
    setForm((prev) => {
      const valid = ensureValidLicenseSelection(
        prev.licenseType,
        licenseOptions,
      );
      return prev.licenseType === valid
        ? prev
        : { ...prev, licenseType: valid };
    });
  }, [licenseOptions]);

  // Initialize products with default quantities
  React.useEffect(() => {
    if (config.products && config.products.length > 0) {
      const initialProducts = {};
      config.products.forEach((product) => {
        initialProducts[product.code] = 0;
      });
      setForm((prev) => ({ ...prev, products: initialProducts }));
    }
  }, []);

  const handleLicenseChange = (e) => {
    setForm((prev) => ({ ...prev, licenseType: e.target.value }));
  };

  const handleProductQuantityChange = (productCode, quantity) => {
    setForm((prev) => {
      const newProducts = { ...prev.products };

      // Handle dependent quantity logic for pager products
      if (productCode === "PAGER-HOLDER-SYSTEM") {
        // When PAGER-HOLDER-SYSTEM changes
        if (quantity > 0) {
          // Ensure PAGER-SYSTEM has at least 1
          if ((newProducts["PAGER-SYSTEM"] || 0) === 0) {
            newProducts["PAGER-SYSTEM"] = 1;
          }
        } else if (quantity === 0) {
          // Reset PAGER-SYSTEM to 0 when PAGER-HOLDER-SYSTEM is removed
          newProducts["PAGER-SYSTEM"] = 0;
        }
      } else if (productCode === "PAGER-SYSTEM") {
        // When PAGER-SYSTEM changes, check if PAGER-HOLDER-SYSTEM is selected
        const pagerHolderQuantity = newProducts["PAGER-HOLDER-SYSTEM"] || 0;
        if (pagerHolderQuantity > 0 && quantity < 1) {
          // Prevent PAGER-SYSTEM from going below 1 when PAGER-HOLDER-SYSTEM is selected
          quantity = 1;
        }
      }

      // Set the requested product quantity
      newProducts[productCode] = quantity;

      return {
        ...prev,
        products: newProducts,
      };
    });
  };
  const handleKioskCountChange = (e) => {
    let val = e.target.value;
    // allow empty string while typing
    if (val === "") {
      setForm((prev) => ({ ...prev, kioskCount: "" }));
      return;
    }
    let num = parseInt(val, 10);
    if (Number.isNaN(num)) num = 1;
    if (num < 1) num = 1;
    if (num > 100) num = 100;
    setForm((prev) => ({ ...prev, kioskCount: num }));
  };

  const totals = useMemo(
    () => calculateTotals(form, config, partnerId),
    [form, partnerId],
  );
  const tiers = (config?.kioskCountDiscounts?.tiers || []).map((tier) => ({
    min: Number(tier?.min ?? 0),
    max:
      tier?.max === null || tier?.max === undefined ? null : Number(tier.max),
    discountPercent:
      typeof tier?.discountPercent === "number"
        ? Number(tier.discountPercent)
        : null,
    note: tier?.note || null,
  }));
  const labelForTier = (tier) => {
    if (!tier) return "";
    if (tier.max === null) {
      const lang = t?.i18n?.language || "sk";
      const note =
        (tier.note && (tier.note[lang] || tier.note.en || tier.note.sk)) ||
        null;
      return note;
    }
    return `${tier.min}–${tier.max}`;
  };
  const commitmentOptions = config?.commitmentDiscounts?.options || {};
  const orderedCommitmentKeys = ["monthly", "12m", "24m"].filter(
    (k) => commitmentOptions[k],
  );
  const handleCommitmentChange = (e) => {
    setForm((prev) => ({ ...prev, commitmentOptionKey: e.target.value }));
  };
  const sanitizeReal = (value) => {
    if (value === "") return "";
    // replace comma with dot, remove invalid chars, keep first dot
    let v = String(value).replace(",", ".");
    v = v.replace(/[^0-9.]/g, "");
    const parts = v.split(".");
    if (parts.length > 2) {
      v = parts.shift() + "." + parts.join("");
    }
    const num = Number(v);
    if (Number.isNaN(num) || num < 0) return 0;
    return num;
  };
  const handleInstallationChange = (key) => (e) => {
    const raw = e.target.value;
    if (raw === "") {
      setForm((prev) => ({
        ...prev,
        installation: {
          ...prev.installation,
          [key]: "",
          ...(key === "km" ? { kmManualOverride: true } : {}),
          ...(key === "km" ? { travelKm: "" } : {}),
        },
      }));
      return;
    }
    const num = sanitizeReal(raw);
    setForm((prev) => ({
      ...prev,
      installation: {
        ...prev.installation,
        [key]: num,
        ...(key === "km" ? { kmManualOverride: true } : {}),
        ...(key === "km" ? { travelKm: num } : {}),
      },
    }));
  };
  const commitmentLabel = (key) => {
    const opt = commitmentOptions[key];
    if (!opt) return key;
    if (key === "monthly") {
      return `${t("pages.priceCalculator.monthly", {
        defaultValue: "Mesačne",
      })} (${t("pages.priceCalculator.noDiscount", {
        defaultValue: "bez zľavy",
      })})`;
    }
    const months = opt.months || 0;
    const disc =
      typeof opt.discountPercent === "number" ? opt.discountPercent : 0;
    return `${months} ${t("pages.priceCalculator.months", {
      defaultValue: "mesiacov",
    })} (-${disc}%)`;
  };
  // Places autocomplete and distance state/handlers
  const addressInputRef = useRef(null);
  const [placeError, setPlaceError] = useState(null);
  const [distanceLoading, setDistanceLoading] = useState(false);
  const [computedKm, setComputedKm] = useState(null);
  const [originalComputedKm, setOriginalComputedKm] = useState(null);

  const initAutocomplete = async () => {
    try {
      setPlaceError(null);
      const google = await loadGoogleMapsPlaces(MAPS_API_KEY);
      if (!addressInputRef.current) return;
      // helper: compute driving distance using client-side Distance Matrix
      const computeDistanceClient = async ({
        origin,
        destinationPlaceId,
        destinationAddress,
      }) => {
        return new Promise((resolve, reject) => {
          try {
            const svc = new google.maps.DistanceMatrixService();
            svc.getDistanceMatrix(
              {
                origins: [origin],
                destinations: destinationPlaceId
                  ? [{ placeId: destinationPlaceId }]
                  : [destinationAddress],
                travelMode: google.maps.TravelMode.DRIVING,
                unitSystem: google.maps.UnitSystem.METRIC,
              },
              (res, status) => {
                if (status !== "OK" || !res || !res.rows?.[0]?.elements?.[0]) {
                  return reject(new Error(status || "NO_RESULT"));
                }
                const el = res.rows[0].elements[0];
                if (el.status !== "OK") {
                  return reject(new Error(el.status || "NO_ELEMENT"));
                }
                const km = (el.distance?.value || 0) / 1000;
                const durationMinutes = (el.duration?.value || 0) / 60;
                resolve({ km, durationMinutes, source: "google-js" });
              },
            );
          } catch (e) {
            reject(e);
          }
        });
      };
      const autocomplete = new google.maps.places.Autocomplete(
        addressInputRef.current,
        {
          fields: ["place_id", "formatted_address"],
          // componentRestrictions: { country: ["sk","cz"] },
          types: ["geocode"],
        },
      );
      autocomplete.addListener("place_changed", async () => {
        const place = autocomplete.getPlace();
        if (!place || !place.place_id) {
          setPlaceError(
            t("pages.priceCalculator.installationAddressValidation", {
              defaultValue: "Vyberte miesto zo zoznamu",
            }),
          );
          return;
        }
        const description =
          place.formatted_address || addressInputRef.current.value || "";
        setForm((prev) => ({
          ...prev,
          installation: {
            ...prev.installation,
            addressText: description,
            placeId: place.place_id,
            kmManualOverride: false,
          },
        }));
        setDistanceLoading(true);
        try {
          const res = await computeDistanceClient({
            origin: ORIGIN_INSTALLATION_ADDRESS,
            destinationPlaceId: place.place_id,
            destinationAddress: description,
          });
          const km = Number(res?.km);
          if (Number.isFinite(km) && km > 0) {
            const rounded = Math.round(km * 10) / 10;
            const doubled = Math.round(rounded * 2 * 10) / 10;
            setComputedKm(doubled);
            setOriginalComputedKm(rounded);
            setForm((prev) => {
              if (prev.installation.kmManualOverride) {
                return prev;
              }
              return {
                ...prev,
                installation: {
                  ...prev.installation,
                  km: doubled,
                  travelKm: doubled,
                },
              };
            });
          } else {
            setPlaceError(
              t("common.error", {
                defaultValue: "Nepodarilo sa získať vzdialenosť.",
              }),
            );
          }
        } catch (e) {
          setPlaceError(
            t("common.error", {
              defaultValue: "Nepodarilo sa získať vzdialenosť.",
            }),
          );
        } finally {
          setDistanceLoading(false);
        }
      });
    } catch (e) {
      setPlaceError(
        t("common.error", {
          defaultValue: "Chyba pri načítaní služby Places.",
        }),
      );
    }
  };
  const formatEuro = (val) => {
    if (val === null || val === undefined) return "—";
    const num = Number(val);
    if (Number.isNaN(num)) return "—";
    return `${num.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} €`;
  };

  const isCz = form.productsMarket === "cz";
  const czRate = Number(form.czExchangeRate);
  const czRateValid = !Number.isNaN(czRate) && czRate > 0;
  const displayCzk =
    isCz && form.displayCurrency === "Kč" && czRateValid;

  const formatPrice = (val) => {
    if (val === null || val === undefined) return "—";
    const num = Number(val);
    if (Number.isNaN(num)) return "—";
    if (displayCzk) {
      const czk = toCzk(num, form.czExchangeRate);
      return czk !== null ? `${czk.toLocaleString("cs-CZ")} Kč` : "—";
    }
    return formatEuro(val);
  };

  const installationFeesConfig = config?.installationFees || {};
  const systemSetupConfig = installationFeesConfig.systemSetup || {};
  const systemSetupNote = t("pages.priceCalculator.systemSetupInfo", {
    defaultValue: `Cena zahŕňa základ pre 1 ks a príplatok za každý ďalší kiosk. (${formatPrice(
      systemSetupConfig.basePrice,
    )} za 1 ks, každý ďalší ks + ${formatPrice(
      systemSetupConfig.additionalUnitPrice,
    )})`,
  });
  const validKioskCount =
    Number.isInteger(form.kioskCount) &&
    form.kioskCount >= 1 &&
    form.kioskCount <= 100;
  const customerPerKioskBase = validKioskCount
    ? totals.customerMonthlyBase / form.kioskCount
    : null;
  const partnerPerKioskBase =
    validKioskCount && totals.partnerMonthlyBase !== null
      ? totals.partnerMonthlyBase / form.kioskCount
      : null;
  const customerPerKioskFinal =
    validKioskCount && totals.customerMonthlyFinal !== null
      ? totals.customerMonthlyFinal / form.kioskCount
      : null;
  const partnerPerKioskFinal =
    validKioskCount && totals.partnerMonthlyFinal !== null
      ? totals.partnerMonthlyFinal / form.kioskCount
      : null;
  const instTotals = useMemo(
    () => calculateInstallationTotals(form, config),
    [form],
  );

  // Calculate kiosk/hardware totals
  const kioskTotals = useMemo(() => {
    const selectedProducts =
      activeProducts?.filter(
        (product) => (form.products[product.code] || 0) > 0,
      ) || [];

    const mocTotal = selectedProducts.reduce((sum, product) => {
      const price = getProductMocPrice(product, form.productsMarket);
      const quantity = form.products[product.code] || 0;
      const itemTotal = price * quantity;
      return sum + (isNaN(itemTotal) ? 0 : itemTotal);
    }, 0);

    const vocTotal =
      partnerId &&
      selectedProducts.some(
        (product) =>
          product.partners &&
          Object.values(product.partners).some(
            (p) => Number(p.id) === Number(partnerId),
          ),
      )
        ? selectedProducts.reduce((sum, product) => {
            const vocPrice = getProductVocPrice(
              product,
              partnerId,
              form.productsMarket,
            );
            const quantity = form.products[product.code] || 0;
            const itemTotal = vocPrice ? vocPrice * quantity : 0;
            return sum + (isNaN(itemTotal) ? 0 : itemTotal);
          }, 0)
        : 0;

    return { moc: mocTotal, voc: vocTotal };
  }, [activeProducts, form.products, form.productsMarket, partnerId]);

  // Auto-sync kioskCount with total selected kiosk products count
  useEffect(() => {
    const kioskCount = Object.entries(form.products).reduce(
      (sum, [productCode, quantity]) => {
        if (!quantity || quantity <= 0) return sum;
        const product = activeProducts?.find((p) => p.code === productCode);
        if (product && product.productType === "kiosk") {
          return sum + quantity;
        }
        return sum;
      },
      0,
    );

    if (form.kioskCount !== kioskCount) {
      setForm((prev) => ({ ...prev, kioskCount }));
    }
  }, [form.products, form.kioskCount, activeProducts]);

  const grand = useMemo(
    () => calculateGrandTotals(totals, instTotals, kioskTotals),
    [totals, instTotals, kioskTotals],
  );
  const invalidPlaceSelection =
    !!form.installation.addressText && !form.installation.placeId;
  const scrollToElement = (elementId) => {
    const element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const handleGenerateQuote = () => {
    // Reset validation errors
    setValidationErrors({
      licenseType: false,
      kioskCount: false,
      address: false,
    });

    // Validation
    const licenseKey = form.licenseType;
    const kc = Number(form.kioskCount);
    const commitmentKey = form.commitmentOptionKey;
    const addressText = form.installation.addressText?.trim();
    const hasValidAddress = addressText && form.installation.placeId;

    const errors = {
      licenseType: !licenseKey,
      kioskCount: !Number.isInteger(kc) || kc < 1 || kc > 100 || kc === 0,
      address: !hasValidAddress,
    };

    setValidationErrors(errors);

    // Find first error and scroll to it
    if (errors.licenseType) {
      scrollToElement("license-type-section");
      return;
    }
    if (errors.kioskCount) {
      scrollToElement("kiosk-count-section");
      return;
    }
    if (errors.address) {
      scrollToElement("installation-section");
      return;
    }
    // snapshot data for quote page
    const snapshot = {
      generatedAt: new Date().toISOString(),
      selection: {
        licenseKey,
        kioskCount: kc,
        commitmentKey,
      },
      products: form.products || {},
      productsMarket: form.productsMarket || "sk",
      displayCurrency: form.displayCurrency || "EUR",
      czExchangeRate: form.czExchangeRate ?? 25,
      installation: {
        // Always include current field values; quote will validate presence
        place: {
          formattedAddress: form.installation.addressText || "",
          placeId: form.installation.placeId || "",
        },
        // Backward compatibility alias
        address: {
          formattedAddress: form.installation.addressText || "",
          placeId: form.installation.placeId || "",
        },
        travelKm: form.installation.travelKm ?? form.installation.km ?? 0,
        km: form.installation.km ?? 0,
        installHours: form.installation.installHours ?? 0,
        installationHours: form.installation.installHours ?? 0,
        systemSetupCount: kc,
      },
    };
    const quoteId =
      "quote_" + Date.now() + "_" + Math.random().toString(36).slice(2);
    try {
      sessionStorage.setItem(quoteId, JSON.stringify(snapshot));
    } catch {}
    try {
      localStorage.setItem(quoteId, JSON.stringify(snapshot));
      localStorage.setItem("quote_last", JSON.stringify(snapshot));
    } catch {}
    const url = `/price-calculator/quote?id=${encodeURIComponent(quoteId)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <Paper variant="outlined">
      <Box sx={{ mt: 1 }}>
        <Alert
          severity="info"
          sx={{
            minHeight: 200,
            display: "flex",
            flexDirection: "column",
            "& .MuiAlert-message": {
              flex: 1,
              display: "flex",
              flexDirection: "column",
            },
          }}
        >
          <AlertTitle>{t("pages.priceCalculator.infobox.title")}</AlertTitle>
          {t("pages.priceCalculator.infobox.content1")}
          <br />
          {t("pages.priceCalculator.infobox.content2")}
          <br />
          {t("pages.priceCalculator.infobox.content3")}
          <br />
          {t("pages.priceCalculator.infobox.content4")}
          <br />
          {t("pages.priceCalculator.infobox.content5")}
        </Alert>
      </Box>
      <Box sx={{ p: 2 }}>
        <Stack spacing={3}>
          {/* Market Selection */}
          <Box>
            <Typography variant="h6" sx={{ mb: 0.5 }}>
              {t("pages.priceCalculator.market", {
                defaultValue: "Trh",
              })}
            </Typography>
            <RadioGroup
              row
              name="productsMarket"
              value={form.productsMarket}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  productsMarket: e.target.value,
                }))
              }
            >
              <FormControlLabel value="sk" control={<Radio />} label="SK" />
              <FormControlLabel value="cz" control={<Radio />} label="CZ" />
            </RadioGroup>
            {isCz && (
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mt: 2 }}>
                <TextField
                  type="number"
                  size="small"
                  label={t("pages.priceCalculator.exchangeRateCzkEur", {
                    defaultValue: "Exchange rate (CZK / EUR)",
                  })}
                  value={form.czExchangeRate}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      czExchangeRate: e.target.value,
                    }))
                  }
                  inputProps={{ min: 0.01, step: 0.01 }}
                  sx={{ width: 220 }}
                  error={isCz && !czRateValid && form.czExchangeRate !== ""}
                  helperText={
                    isCz && !czRateValid && form.czExchangeRate !== ""
                      ? t("pages.priceCalculator.exchangeRateInvalid", {
                          defaultValue: "Enter a value greater than 0",
                        })
                      : null
                  }
                />
                <Typography variant="body2" color="text.secondary">
                  {t("pages.priceCalculator.displayCurrency", {
                    defaultValue: "Display",
                  })}
                </Typography>
                <ToggleButtonGroup
                  value={
                    !czRateValid ? "EUR" : form.displayCurrency
                  }
                  exclusive
                  onChange={(_, newVal) => {
                    if (newVal == null || !czRateValid) return;
                    setForm((prev) => ({ ...prev, displayCurrency: newVal }));
                  }}
                  size="small"
                >
                  <ToggleButton value="EUR" aria-label="EUR">
                    EUR
                  </ToggleButton>
                  <ToggleButton
                    value="Kč"
                    aria-label="Kč"
                    disabled={!czRateValid}
                  >
                    Kč
                  </ToggleButton>
                </ToggleButtonGroup>
              </Stack>
            )}
          </Box>

          <Box>
            <Typography variant="h6" sx={{ mb: 0.5 }}>
              {t("pages.priceCalculator.products", {
                defaultValue: "Hardware",
              })}
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "repeat(1, 1fr)",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(3, 1fr)",
                  lg: "repeat(6, 1fr)",
                },
                gap: 2,
                justifyItems: "center",
                maxWidth: "100%",
              }}
            >
              {activeProducts?.map((product) => (
                <ProductTile
                  key={product.code}
                  product={product}
                  quantity={form.products[product.code] || 0}
                  onQuantityChange={handleProductQuantityChange}
                  t={t}
                  loggedPartnerId={loggedPartnerId}
                  market={form.productsMarket}
                  pagerHolderSelected={
                    (form.products["PAGER-HOLDER-SYSTEM"] || 0) > 0
                  }
                  formatPrice={formatPrice}
                />
              ))}
            </Box>
            <ProductsSummaryTable
              products={form.products}
              selectedProducts={selectedProducts}
              total={productsTotal}
              vocTotal={vocProductsTotal}
              hasAnyVoc={hasAnyVoc}
              loggedPartnerId={loggedPartnerId}
              market={form.productsMarket}
              t={t}
              formatPrice={formatPrice}
            />
          </Box>

          {/* Main row: Kiosk Count, License Type, and Commitment side-by-side */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(auto-fit, minmax(300px, 1fr))",
                lg: "1fr 1fr 1fr",
              },
              gap: 3,
            }}
          >
            {/* Kiosk Count */}
            <Box id="kiosk-count-section">
              <Typography variant="h6" sx={{ mb: 0.5 }}>
                {t("pages.priceCalculator.kioskCount", {
                  defaultValue: "Počet kioskov",
                })}
                <span style={{ color: "red" }}>*</span>
              </Typography>
              {validationErrors.kioskCount && (
                <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                  {t("pages.priceCalculator.kioskRequired", {
                    defaultValue: "Vyberte aspoň jeden kiosk",
                  })}
                </Typography>
              )}
              <Stack direction="row" spacing={2} alignItems="center">
                <input
                  type="number"
                  min={1}
                  max={100}
                  step={1}
                  value={form.kioskCount}
                  readOnly
                  disabled
                  style={{
                    width: 100,
                    padding: 8,
                    borderRadius: 4,
                    border: "1px solid #ccc",
                    backgroundColor: "#f5f5f5",
                    color: "#666",
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  {t("pages.priceCalculator.kioskCountDerived", {
                    defaultValue:
                      "Počet kioskov je odvodený z počtu vybraných zariadení",
                  })}
                </Typography>
              </Stack>
              <Box sx={{ mt: 1 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 0.5 }}
                >
                  {t("pages.priceCalculator.kioskTiersInfo", {
                    defaultValue: "Zľavy podľa počtu kioskov:",
                  })}
                </Typography>
                <Stack spacing={0.5}>
                  {tiers.map((tier, idx) => (
                    <Typography
                      key={idx}
                      variant="caption"
                      color="text.secondary"
                    >
                      {labelForTier(tier)}{" "}
                      {tier.max === null || tier.discountPercent === null
                        ? ""
                        : `-${tier.discountPercent}%`}
                    </Typography>
                  ))}
                </Stack>
              </Box>
            </Box>

            {/* License Type */}
            <Box id="license-type-section">
              <Typography variant="h6" sx={{ mb: 0.5 }}>
                {t("pages.priceCalculator.licenseType", {
                  defaultValue: "Typ licencie",
                })}
                <span style={{ color: "red" }}>*</span>
              </Typography>
              {validationErrors.licenseType && (
                <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                  {t("pages.priceCalculator.licenseRequired", {
                    defaultValue: "Prosím, vyberte typ licencie",
                  })}
                </Typography>
              )}
              <RadioGroup
                name="licenseType"
                value={form.licenseType}
                onChange={handleLicenseChange}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                {licenseOptions.map((opt) => (
                  <FormControlLabel
                    key={opt.key}
                    value={opt.key}
                    control={<Radio />}
                    label={opt.name || opt.key}
                  />
                ))}
              </RadioGroup>
            </Box>

            {/* Commitment */}
            <Box>
              <Typography variant="h6" sx={{ mb: 0.5 }}>
                {t("pages.priceCalculator.commitment", {
                  defaultValue: "Platba / viazanosť",
                })}
              </Typography>
              <RadioGroup
                name="commitment"
                value={form.commitmentOptionKey}
                onChange={handleCommitmentChange}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                {orderedCommitmentKeys.map((key) => (
                  <FormControlLabel
                    key={key}
                    value={key}
                    control={<Radio />}
                    label={commitmentLabel(key)}
                  />
                ))}
              </RadioGroup>
            </Box>
          </Box>

          {/* Installation - separate section */}
          <Box id="installation-section">
            <Box>
              <Typography variant="h6" sx={{ mb: 0.5 }}>
                {t("pages.priceCalculator.installation", {
                  defaultValue: "Inštalácia",
                })}
                <span style={{ color: "red" }}>*</span>
              </Typography>
              {validationErrors.address && (
                <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                  {t("pages.priceCalculator.addressRequired", {
                    defaultValue: "Vyberte platnú adresu inštalácie",
                  })}
                </Typography>
              )}
              <Stack spacing={1}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography sx={{ minWidth: 220 }}>
                    {t("pages.priceCalculator.installationAddress", {
                      defaultValue: "Adresa inštalácie",
                    })}
                  </Typography>
                  <Box sx={{ flex: 1, position: "relative" }}>
                    <input
                      type="text"
                      ref={addressInputRef}
                      value={form.installation.addressText}
                      onFocus={() => {
                        if (
                          addressInputRef.current &&
                          !addressInputRef.current._autocompleteInit
                        ) {
                          addressInputRef.current._autocompleteInit = true;
                          initAutocomplete();
                        }
                      }}
                      onChange={(e) => {
                        const value = e.target.value;
                        setPlaceError(null);
                        setForm((prev) => ({
                          ...prev,
                          installation: {
                            ...prev.installation,
                            addressText: value,
                            placeId: "",
                          },
                        }));
                      }}
                      placeholder={t(
                        "pages.priceCalculator.installationAddressPlaceholder",
                        {
                          defaultValue: "Zadajte adresu...",
                        },
                      )}
                      style={{
                        width: "100%",
                        padding: 8,
                        borderRadius: 4,
                        border: invalidPlaceSelection
                          ? "1px solid #d32f2f"
                          : "1px solid #ccc",
                      }}
                    />
                  </Box>
                </Stack>
                {invalidPlaceSelection ? (
                  <Typography variant="caption" color="error">
                    {t("pages.priceCalculator.installationAddressValidation", {
                      defaultValue: "Vyberte miesto zo zoznamu",
                    })}
                  </Typography>
                ) : null}
                {form.installation.placeId ? (
                  <Typography variant="caption" color="text.secondary">
                    {t("pages.priceCalculator.selectedAddress", {
                      defaultValue: `Vybraná adresa: ${form.installation.addressText}`,
                    })}
                  </Typography>
                ) : null}
                <Typography
                  variant="caption"
                  color={placeError ? "error" : "text.secondary"}
                >
                  {placeError
                    ? placeError
                    : distanceLoading
                      ? t("pages.priceCalculator.distanceCalculating", {
                          defaultValue: "Počítam vzdialenosť...",
                        })
                      : computedKm !== null
                        ? t("pages.priceCalculator.distanceFromOrigin", {
                            defaultValue: `Vzdialenosť z Vrbového: ${originalComputedKm} km`,
                          })
                        : ""}
                </Typography>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography sx={{ minWidth: 220 }}>
                    {t("pages.priceCalculator.travelKm", {
                      defaultValue: "Cestovné (km)",
                    })}
                  </Typography>
                  <input
                    type="number"
                    min={0}
                    step={1}
                    value={form.installation.km}
                    onChange={handleInstallationChange("km")}
                    style={{
                      width: 120,
                      padding: 8,
                      borderRadius: 4,
                      border: "1px solid #ccc",
                    }}
                  />
                </Stack>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography sx={{ minWidth: 220 }}>
                    {t("pages.priceCalculator.installHours", {
                      defaultValue: "Inštalačné práce (hod)",
                    })}
                  </Typography>
                  <input
                    type="number"
                    min={2}
                    step={1}
                    value={form.installation.installHours}
                    onChange={handleInstallationChange("installHours")}
                    style={{
                      width: 120,
                      padding: 8,
                      borderRadius: 4,
                      border: "1px solid #ccc",
                    }}
                  />
                </Stack>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography sx={{ minWidth: 220 }}>
                    {t("pages.priceCalculator.systemSetup", {
                      defaultValue: "Nastavenie systému",
                    })}
                  </Typography>
                  <Typography variant="body2">
                    {instTotals.systemSetupCost !== null
                      ? formatPrice(instTotals.systemSetupCost)
                      : "—"}
                  </Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  {systemSetupNote}
                </Typography>
              </Stack>
            </Box>
          </Box>

          <Divider />

          {validKioskCount && (
            <Box>
              <Typography variant="h5" fontWeight="bold" sx={{ mb: 1 }}>
                {t("pages.priceCalculator.total", {
                  defaultValue: "Spolu",
                })}
              </Typography>
              <Typography variant="h6" sx={{ mb: 0.5 }}>
                {t("pages.priceCalculator.licenseSummary", {
                  defaultValue: "Licencie",
                })}
              </Typography>
              <TableContainer>
                <Table size="small">
                  <colgroup>
                    <col style={{ width: "70%" }} />
                    <col style={{ width: partnerId ? "15%" : "30%" }} />
                    {partnerId ? <col style={{ width: "15%" }} /> : null}
                  </colgroup>
                  <TableHead>
                    <TableRow>
                      <TableCell />
                      <TableCell>MOC</TableCell>
                      {partnerId ? <TableCell>VOC</TableCell> : null}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {totals.isIndividualPricing ? (
                      <TableRow>
                        <TableCell>—</TableCell>
                        {partnerId ? <TableCell>—</TableCell> : null}
                      </TableRow>
                    ) : (
                      <>
                        <TableRow>
                          <TableCell>
                            {t("pages.priceCalculator.perKioskBase", {
                              defaultValue: "Cena za 1 ks (základ)",
                            })}
                          </TableCell>
                          <TableCell>
                            {formatPrice(customerPerKioskBase)}
                          </TableCell>
                          {partnerId ? (
                            <TableCell>
                              {formatPrice(partnerPerKioskBase)}
                            </TableCell>
                          ) : null}
                        </TableRow>
                        <TableRow>
                          <TableCell>
                            {t("pages.priceCalculator.baseMonthly", {
                              defaultValue: "Základ mesačne",
                            })}
                          </TableCell>
                          <TableCell>
                            {formatPrice(totals.customerMonthlyBase)}
                          </TableCell>
                          {partnerId ? (
                            <TableCell>
                              {formatPrice(totals.partnerMonthlyBase)}
                            </TableCell>
                          ) : null}
                        </TableRow>
                        <TableRow>
                          <TableCell>
                            {t("pages.priceCalculator.kioskDiscount", {
                              defaultValue: "Zľava (počet ks)",
                            })}{" "}
                            ({totals.kioskDiscountPercent}%)
                          </TableCell>
                          <TableCell>
                            {totals.customerKioskDiscountAmountMonthly > 0 ? (
                              <b>
                                -
                                {formatPrice(
                                  totals.customerKioskDiscountAmountMonthly,
                                )}
                              </b>
                            ) : (
                              "—"
                            )}
                          </TableCell>
                          {partnerId ? (
                            <TableCell>
                              {totals.partnerKioskDiscountAmountMonthly > 0 ? (
                                <b>
                                  -
                                  {formatPrice(
                                    totals.partnerKioskDiscountAmountMonthly,
                                  )}
                                </b>
                              ) : (
                                "—"
                              )}
                            </TableCell>
                          ) : null}
                        </TableRow>
                        {totals.customerCommitmentDiscountAmountMonthly > 0 ||
                        (partnerId &&
                          totals.partnerCommitmentDiscountAmountMonthly > 0) ? (
                          <TableRow>
                            <TableCell>
                              {t("pages.priceCalculator.commitmentDiscount", {
                                defaultValue: "Zľava (viazanosť)",
                              })}{" "}
                              ({totals.commitmentDiscountPercent}%)
                            </TableCell>
                            <TableCell>
                              {totals.customerCommitmentDiscountAmountMonthly >
                              0 ? (
                                <b>
                                  -
                                  {formatPrice(
                                    totals.customerCommitmentDiscountAmountMonthly,
                                  )}
                                </b>
                              ) : (
                                "—"
                              )}
                            </TableCell>
                            {partnerId ? (
                              <TableCell>
                                {totals.partnerCommitmentDiscountAmountMonthly >
                                0 ? (
                                  <b>
                                    -
                                    {formatPrice(
                                      totals.partnerCommitmentDiscountAmountMonthly,
                                    )}
                                  </b>
                                ) : (
                                  "—"
                                )}
                              </TableCell>
                            ) : null}
                          </TableRow>
                        ) : null}
                        <TableRow>
                          <TableCell>
                            {t("pages.priceCalculator.finalMonthly", {
                              defaultValue: "Spolu mesačne",
                            })}
                          </TableCell>
                          <TableCell>
                            <b>
                              {formatPrice(
                                totals.customerMonthlyFinal ??
                                  totals.customerMonthlyBase,
                              )}
                            </b>
                          </TableCell>
                          {partnerId ? (
                            <TableCell>
                              <b>
                                {formatPrice(
                                  totals.partnerMonthlyFinal ??
                                    totals.partnerMonthlyBase,
                                )}
                              </b>
                            </TableCell>
                          ) : null}
                        </TableRow>
                        <TableRow>
                          <TableCell>
                            {t("pages.priceCalculator.perKioskFinal", {
                              defaultValue: "Cena za 1 ks (po zľavách)",
                            })}
                          </TableCell>
                          <TableCell>
                            <b>{formatPrice(customerPerKioskFinal)}</b>
                          </TableCell>
                          {partnerId ? (
                            <TableCell>
                              <b>{formatPrice(partnerPerKioskFinal)}</b>
                            </TableCell>
                          ) : null}
                        </TableRow>
                        {validKioskCount && form.kioskCount > 1 ? (
                          <>
                            <TableRow>
                              <TableCell>
                                {t("pages.priceCalculator.totalForCount", {
                                  defaultValue: "Cena spolu",
                                })}{" "}
                                ({form.kioskCount}{" "}
                                {t("pages.priceCalculator.kiosks", {
                                  defaultValue: "ks",
                                })}
                                )
                              </TableCell>
                              <TableCell>
                                <b>
                                  {formatPrice(
                                    totals.customerMonthlyFinal ??
                                      totals.customerMonthlyBase,
                                  )}
                                </b>
                              </TableCell>
                              {partnerId ? (
                                <TableCell>
                                  <b>
                                    {formatPrice(
                                      totals.partnerMonthlyFinal ??
                                        totals.partnerMonthlyBase,
                                    )}
                                  </b>
                                </TableCell>
                              ) : null}
                            </TableRow>
                          </>
                        ) : null}
                        {totals.commitmentMonths > 1 ? (
                          <>
                            <TableRow>
                              <TableCell>
                                {t("pages.priceCalculator.periodSum", {
                                  defaultValue: "Suma za obdobie",
                                })}{" "}
                                ({totals.commitmentMonths}{" "}
                                {t("pages.priceCalculator.months", {
                                  defaultValue: "mesiacov",
                                })}
                                )
                              </TableCell>
                              <TableCell>
                                <b>{formatPrice(totals.customerPeriodFinal)}</b>
                              </TableCell>
                              {partnerId ? (
                                <TableCell>
                                  <b>{formatPrice(totals.partnerPeriodFinal)}</b>
                                </TableCell>
                              ) : null}
                            </TableRow>
                            <TableRow>
                              <TableCell>
                                {t("pages.priceCalculator.periodDiscount", {
                                  defaultValue: "Zľava za obdobie",
                                })}
                              </TableCell>
                              <TableCell>
                                <b>
                                  {totals.customerPeriodDiscount > 0
                                    ? `-${formatPrice(
                                        totals.customerPeriodDiscount,
                                      )}`
                                    : "—"}
                                </b>
                              </TableCell>
                              {partnerId ? (
                                <TableCell>
                                  <b>
                                    {totals.partnerPeriodDiscount > 0
                                      ? `-${formatPrice(
                                          totals.partnerPeriodDiscount,
                                        )}`
                                      : "—"}
                                  </b>
                                </TableCell>
                              ) : null}
                            </TableRow>
                          </>
                        ) : null}
                      </>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Divider sx={{ my: 1 }} />
              <Typography variant="h5" sx={{ mb: 1 }}>
                {t("pages.priceCalculator.installationSummary", {
                  defaultValue: "Inštalácia (jednorazovo)",
                })}
              </Typography>
              <TableContainer>
                <Table size="small">
                  <colgroup>
                    <col style={{ width: "70%" }} />
                    <col style={{ width: "30%" }} />
                  </colgroup>
                  <TableBody>
                    <TableRow>
                      <TableCell>
                        {t("pages.priceCalculator.travel", {
                          defaultValue: "Cestovné",
                        })}
                      </TableCell>
                      <TableCell>{formatPrice(instTotals.travelCost)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        {t("pages.priceCalculator.installWork", {
                          defaultValue: "Inštalačné práce",
                        })}
                      </TableCell>
                      <TableCell>
                        {formatPrice(instTotals.installWorkCost)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        {t("pages.priceCalculator.systemSetup", {
                          defaultValue: "Nastavenie systému",
                        })}
                      </TableCell>
                      <TableCell>
                        {instTotals.systemSetupCost !== null
                          ? formatPrice(instTotals.systemSetupCost)
                          : "—"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        {t("pages.priceCalculator.installationTotal", {
                          defaultValue: "Spolu inštalácia",
                        })}
                      </TableCell>
                      <TableCell>
                        <b>{formatPrice(instTotals.installationTotal)}</b>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              <Divider sx={{ my: 2 }} />
              <Typography variant="h5" fontWeight="bold" sx={{ mb: 1 }}>
                {t("pages.priceCalculator.grandTotal", {
                  defaultValue: "Celkový súhrn",
                })}
              </Typography>
              <TableContainer>
                <Table size="small">
                  <colgroup>
                    <col style={{ width: "60%" }} />
                    <col style={{ width: partnerId ? "20%" : "40%" }} />
                    {partnerId ? <col style={{ width: "20%" }} /> : null}
                  </colgroup>
                  <TableBody>
                    <TableRow>
                      <TableCell>
                        {t("pages.priceCalculator.monthly", {
                          defaultValue: "Mesačne",
                        })}
                      </TableCell>
                      <TableCell>
                        <b>{formatPrice(grand.customerMonthly)}</b>
                      </TableCell>
                      {partnerId ? (
                        <TableCell>
                          <b>{formatPrice(grand.partnerMonthly)}</b>
                        </TableCell>
                      ) : null}
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        {t("pages.priceCalculator.installOnce", {
                          defaultValue: "Jednorazovo (inštalácia)",
                        })}
                      </TableCell>
                      <TableCell>
                        <b>{formatPrice(grand.installOnce)}</b>
                      </TableCell>
                      {partnerId ? (
                        <TableCell>
                          <b>{formatPrice(grand.installOnce)}</b>
                        </TableCell>
                      ) : null}
                    </TableRow>
                    {grand.kiosksOnce > 0 && (
                      <TableRow>
                        <TableCell>Kiosk</TableCell>
                        <TableCell>
                          <b>{formatPrice(grand.kiosksOnce)}</b>
                        </TableCell>
                        {partnerId ? (
                          <TableCell>
                            <b>{formatPrice(grand.kiosksOnceVoc)}</b>
                          </TableCell>
                        ) : null}
                      </TableRow>
                    )}
                    <TableRow>
                      <TableCell>
                        {t("pages.priceCalculator.total", {
                          defaultValue: "Spolu",
                        })}
                      </TableCell>
                      <TableCell>
                        <b>{formatPrice(grand.oneTimeTotal)}</b>
                      </TableCell>
                      {partnerId ? (
                        <TableCell>
                          <b>{formatPrice(grand.oneTimeTotalVoc)}</b>
                        </TableCell>
                      ) : null}
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        {t("pages.priceCalculator.firstMonth", {
                          defaultValue: "Spolu prvý mesiac",
                        })}
                      </TableCell>
                      <TableCell>
                        <b>{formatPrice(grand.customerFirstMonth)}</b>
                      </TableCell>
                      {partnerId ? (
                        <TableCell>
                          <b>{formatPrice(grand.partnerFirstMonth)}</b>
                        </TableCell>
                      ) : null}
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 1 }}
              >
                Všetky ceny sú uvádzané bez DPH 23%. Pri nákupe viac ako 2
                kioskov pripravíme individuálnu ponuku.
              </Typography>
            </Box>
          )}
          {validKioskCount && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                mt: 2,
              }}
            >
              <button
                type="button"
                onClick={handleGenerateQuote}
                style={{
                  padding: "10px 14px",
                  background: "#1976d2",
                  color: "#fff",
                  border: "none",
                  borderRadius: 4,
                  cursor: "pointer",
                  alignSelf: "flex-start",
                }}
              >
                Vygenerovať ponuku
              </button>
              <Typography variant="caption" color="text.secondary">
                Ponuka sa otvorí v novom okne
              </Typography>
            </Box>
          )}
        </Stack>
      </Box>
    </Paper>
  );
};

export default PriceCalculatorForm;
